import {
  ContextualMenuItemType,
  ICommandBarItemProps,
  IContextualMenuItem,
  VerticalDivider,
  Icon,
} from "@fluentui/react";
import ActionBar, {
  ICustomCommandBarStyles,
} from "components/shared/ActionBar";
import icons from "components/shared/Icons";
import IndexVersionMenu, {
  CurrentItemInfo,
} from "components/shared/IndexVersionMenu";
import WithLoading from "components/shared/WithLoading";
import {
  AgreementObjectEditState,
  AgreementObjectType,
  IAgreementObjectContentInfo,
  IAgreementObjectInfo,
} from "models/agreements";
import { IRevision, RevisionStatus } from "models/revisions";

import { DialogContentType } from "models/dialogs";
import useAgreementObjectDialog from "hooks/useAgreementObjectDialog";
import { environmentConfig } from "config/environmentConfig"; // Used for policy deletion
import {
  getRevisionId,
  getRevisionStatusDisplayName,
  isDraftRevisionVariant,
  isPublishedRevisionVariant,
} from "helpers/revisions";
import { CSSProperties, useEffect, useState } from "react";
import useRouter from "hooks/useRouter";
import { routeDefinitions, RouteComponent } from "router";
import customTheme from "helpers/customTheme";
import useTemplatePreview from "hooks/template/useTemplatePreview";
import { useDispatch } from "react-redux";
import { resultSlotActions } from "store/ResultSlotSlice";
import { FormatDate } from "helpers/dates";
import { useGetCurrentRevisionWithEtagQuery, useLazyGetRevisionsQuery, useLazyGetRequiredConstraintsQuery } from "services/template";
import { ITemplateRevision, IRequiredConstraints } from "models/templates";
import { hasReadOnlyAccess } from "components/AppContent";
import PolicyPublishDialog, { IPolicyPublishData } from "components/policy/PolicyPublishDialog";
import { usePublishPolicyMutation, useWithdrawPolicyMutation, useGetPolicyDetailsQuery } from "services/policy";
import { useTrackingContext } from "./TrackingContext";

type SharedActionBarProps = {
  objectType: AgreementObjectType;
  objectInfo: IAgreementObjectInfo;
  objectContentInfo?: IAgreementObjectContentInfo;
  openEditPanel: (...args: any) => void;
  setCurrentRevision: (revision: IRevision) => void;
  editState?: AgreementObjectEditState;
  disablePublishOverride?: boolean;
  additionalStyles?: {
    versionMenuStyles?: CSSProperties;
    actionBarStyles?: ICustomCommandBarStyles;
  };
  componentInfo?: string;
  onDeleteClick?: () => void;
};

const getAgreementObject: any = (
  objectType: AgreementObjectType,
  objectInfo: IAgreementObjectInfo
) => {
  if (
    objectType === AgreementObjectType.clause ||
    objectType === AgreementObjectType.customClause
  ) {
    return objectInfo?.clause;
  }
  if (objectType === AgreementObjectType.template) {
    return objectInfo?.template;
  }
  if (objectType === AgreementObjectType.policy) {
    return objectInfo?.policy;
  }

  return objectInfo?.clause;
};
// add a little extra padding per command button
const buttonStyle = { root: { padding: "0px 6px" } };
const dividerStyle = {
  divider: { height: "75%" },
};

// Reusable divider component to avoid ESLint error
// eslint-disable-next-line react/function-component-definition
function ActionBarDivider() {
  return <VerticalDivider styles={dividerStyle} />;
}

// eslint-disable-next-line react/function-component-definition
const SharedActionBar = ({
  objectType,
  objectInfo,
  objectContentInfo, // only clauses and custom clauses have 'contents'
  openEditPanel,
  setCurrentRevision,
  editState,
  disablePublishOverride,
  additionalStyles,
  componentInfo,
  onDeleteClick,
}: SharedActionBarProps) => {
  const dispatch = useDispatch();
  const { openDeletionDialog, openUpdateRevisionDialog } =
    useAgreementObjectDialog();
  const { getRouteInfo, goToRoute } = useRouter();
  const routeInfo = getRouteInfo();
  const { trackEvent } = useTrackingContext();
  let { currentRevision } = objectInfo;
  const currentObject: any = getAgreementObject(objectType, objectInfo);
  const { resetConfig } = useTemplatePreview();

  // Policy publish mutation hook
  const [publishPolicy] = usePublishPolicyMutation();
  
  // Policy withdraw mutation hook
  const [withdrawPolicy] = useWithdrawPolicyMutation();
  
  // Fetch policy details for pre-populating publish dialog
  const { data: policyDetails } = useGetPolicyDetailsQuery(
    {
      id: currentObject?.id || "",
      cloud: environmentConfig.cloud,
      environment: environmentConfig.environment,
      policyType: currentObject?.type,
    },
    {
      skip: !currentObject?.id || componentInfo !== "policyEdit",
    }
  );
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  // Lazy query to fetch template revisions
  const [getTemplateRevisions] = useLazyGetRevisionsQuery();
  // Lazy query to fetch required constraints
  const [getRequiredConstraints] = useLazyGetRequiredConstraintsQuery();

  const id = currentRevision?.id;
  let revisionWithEtag: ITemplateRevision = {} as ITemplateRevision;
  let isLoadingRevision: boolean = true;

  if (objectType === AgreementObjectType.template) {
    const templateId = objectInfo.template?.id;
    const { data: revisionEtag, isLoading } =
      useGetCurrentRevisionWithEtagQuery({ templateId, id });
    revisionWithEtag = revisionEtag!;
    isLoadingRevision = isLoading;
  }
  const [currentRevisionWithEtag, setCurrentRevisionWithEtag] = useState<
    IRevision | undefined
  >(undefined);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);

  useEffect(() => {
    if (
      !isLoadingRevision &&
      revisionWithEtag &&
      objectType === AgreementObjectType.template
    ) {
      const etagRevision: IRevision = {
        id: revisionWithEtag.id,
        name: revisionWithEtag.name,
        effectiveDate: revisionWithEtag.effectiveDate,
        status: revisionWithEtag.status,
        createdBy: revisionWithEtag.createdBy,
        createdDate: revisionWithEtag.createdDate,
        modifiedBy: revisionWithEtag.modifiedBy,
        modifiedDate: revisionWithEtag.modifiedDate,
        etag: revisionWithEtag.etag,
        isCloned: revisionWithEtag.isCloned,
        number: revisionWithEtag.number,
      };

      setCurrentRevisionWithEtag(etagRevision);
      currentRevision = etagRevision;
    }
  }, [isLoadingRevision, revisionWithEtag]);
  const getRevisionMenuItemText = (
    revision: IRevision | undefined,
    isLatestGrouping: boolean
  ): string => {
    const revisionStatusStr = getRevisionStatusDisplayName(revision?.status);

    if (isLatestGrouping) {
      const revisionNumber = revision?.number ? ` - v${revision?.number}` : "";
      const additionalInfo =
        revision?.status === RevisionStatus.Pending
          ? ` - ${FormatDate(new Date(revision.effectiveDate))}`
          : revisionNumber;
      return `${revisionStatusStr}${additionalInfo}`;
    }

    if (
      revision?.status === RevisionStatus.Draft ||
      revision?.status === RevisionStatus.Test
    ) {
      return revisionStatusStr;
    }

    if (revision?.name) {
      return revision?.name;
    }

    if (revision?.effectiveDate) {
      return new Date(revision?.effectiveDate).toDateString();
    }

    return "-";
  };

  const getRevisionsAsContextualRevisionMenu = (
    revisions: IRevision[],
    isLatestGrouping: boolean
  ): IContextualMenuItem[] =>
    revisions.map((revision) => ({
      key: getRevisionId(revision),
      text: getRevisionMenuItemText(revision, isLatestGrouping),

      onClick: () => {
        setCurrentRevision(revision);
        if (
          revision.id !== currentRevision?.id &&
          componentInfo !== "TemplateEdit"
        ) {
          dispatch(resultSlotActions.setTriggered({ isTriggered: true }));
          resetConfig();
        }
      },
    }));

  const getRevisionMenuItems = (): IContextualMenuItem[] => {
    const revisions = objectInfo.revisions || [];
    const latestLiveRevisionIndex = revisions.findIndex(
      (r) => r.status === RevisionStatus.Live
    );
    const newGroupCutoffIndex =
      (latestLiveRevisionIndex !== -1
        ? latestLiveRevisionIndex
        : revisions.length) + 1;

    const contextualMenuItems = getRevisionsAsContextualRevisionMenu(
      revisions.slice(0, newGroupCutoffIndex),
      true
    );

    if (newGroupCutoffIndex < revisions.length) {
      contextualMenuItems.push({
        key: "older",
        itemType: ContextualMenuItemType.Header,
        text: "Older",
      });
      contextualMenuItems.push(
        ...getRevisionsAsContextualRevisionMenu(
          revisions.slice(newGroupCutoffIndex, revisions.length),
          false
        )
      );
    }

    // custom clause does not allow creating new revision in Agreement Center
    // but multiple revisions may be created from other apps (eg: Quote Center) interacting with Agreement Center
    if (objectType === AgreementObjectType.customClause) {
      return contextualMenuItems;
    }

    let isNewObject: boolean = false;
    if (objectType === AgreementObjectType.clause) {
      isNewObject = editState === AgreementObjectEditState.NewClause;
    } else if (objectType === AgreementObjectType.template) {
      isNewObject = editState === AgreementObjectEditState.NewTemplate;
    }

    const createNewRevisionDisabled =
      editState === AgreementObjectEditState.NewRevision || hasReadOnlyAccess();
    const isSingleUnpublishedRevision =
      currentRevision?.status === RevisionStatus.Unpublished &&
      revisions.length === 1;

    if (!isNewObject && !isSingleUnpublishedRevision) {
      contextualMenuItems.push(
        ...[
          { key: "divider_1", itemType: ContextualMenuItemType.Divider },
          {
            key: "new-version",
            text: "Create new version",
            disabled: createNewRevisionDisabled,
            buttonStyles: buttonStyle,
            itemProps: {
              styles: {
                root: {
                  color: createNewRevisionDisabled
                    ? customTheme.disabledSemanticColor
                    : customTheme.linkSemanticColor,
                },
              },
            },
            onClick: () => {
              trackEvent(
                `${objectType} create new version option selected from ${routeInfo?.component} page`
              );
              if (objectType === AgreementObjectType.template) {
                openEditPanel(
                  currentObject?.id,
                  currentRevision?.id,
                  AgreementObjectEditState.NewRevision
                );
              } else {
                openEditPanel(
                  {
                    clauseId: currentObject?.id,
                    revisionId: currentRevision?.id,
                  },
                  AgreementObjectEditState.NewRevision
                );
              }
            },
          },
        ]
      );
    }

    return contextualMenuItems;
  };

  const getRevisionMenu = (): JSX.Element => {
    // Policy-specific simple draft badge
    if (componentInfo === "policyEdit") {
      const isPolicyDraft = currentObject?.status === "Draft";
      const isTestPublished = currentObject?.status === "Published" && currentObject?.isTestPolicy;
      // eslint-disable-next-line no-nested-ternary
      const badgeBg = isPolicyDraft ? "#FFF4CE" : isTestPublished ? "#EFF6FC" : "#DFF6DD";
      // eslint-disable-next-line no-nested-ternary
      const badgeColor = isPolicyDraft ? "#323130" : isTestPublished ? "#005A9E" : "#107C10";
      // eslint-disable-next-line no-nested-ternary
      const iconColor = isPolicyDraft ? "#8A8886" : isTestPublished ? "#005A9E" : "#107C10";
      const statusText = isTestPublished ? "Published (Test)" : (currentObject?.status || "Unknown Status");

      return (
        <div style={{
          display: "flex",
          alignItems: "center",
          padding: "6px 12px",
          backgroundColor: badgeBg,
          borderRadius: "2px",
          gap: "8px",
          height: 48,
          border: "none",
          pointerEvents: "none", // Disable interaction
        }}>
          <Icon
            iconName={isPolicyDraft ? "PageEdit" : "Clock"}
            style={{
              fontSize: "16px",
              color: iconColor
            }}
          />
          <span style={{ color: badgeColor, fontWeight: 500, fontSize: 14 }}>
            {statusText}
          </span>
        </div>
      );
    }

    const currentRevisionMenuItem: CurrentItemInfo = {
      text: getRevisionMenuItemText(currentRevision, true),
      status: currentRevision?.status,
      id: getRevisionId(currentRevision),
    };

    const menuItems = getRevisionMenuItems();
    return (
      <IndexVersionMenu
        menuItems={menuItems}
        currentItem={currentRevisionMenuItem}
        additionalStyles={additionalStyles?.versionMenuStyles}
        isCommandBarItem
      />
    );
  };

  const getCommandBarItems = (): ICommandBarItemProps[] => {
    const currentRevisionStatus = currentRevision?.status;
    const revisionNumber = currentRevision?.number
      ? ` - v${currentRevision.number}`
      : "";

    const updateRevisionDialogInfo = {
      objectType,
      objectName: `${currentObject?.name}${revisionNumber}`,
      id: currentObject?.id,
      revision:
        objectType === AgreementObjectType.template
          ? currentRevisionWithEtag!
          : currentRevision!,
    };

    const commandBarItems: ICommandBarItemProps[] = [
      { key: "revisionsMenu", onRender: getRevisionMenu },
    ];

    // Policy-specific logic: Only show Draft, Delete policy, Withdraw (if published), and Publish
    if (componentInfo === "policyEdit") {
      const isPolicyPublished = currentObject?.status === 'Published' || currentObject?.status === 'Live';

      // Check if policy effective date (agreement effective date) is in the past
      // Delete and Withdraw are disabled after the policy effective date
      const isPolicyEffectiveDateInPast = (() => {
        const effectiveDateStr = policyDetails?.agreementEffectiveDate || currentObject?.agreementEffectiveDate;
        if (!effectiveDateStr) {
          return false; // No effective date means not yet set
        }
        try {
          const effectiveDate = new Date(effectiveDateStr);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          effectiveDate.setHours(0, 0, 0, 0);
          return effectiveDate <= today;
        } catch {
          return false;
        }
      })();

      const isActionDisabled = isPolicyEffectiveDateInPast;

      // Withdraw is only allowed before the policy effective date and status should not be 'Publish'
      const isWithdrawDisabled = !isPolicyPublished || isActionDisabled || hasReadOnlyAccess();

      // Delete policy button
      // Only enabled for Draft status, disabled for all other statuses
      const isPolicyDraft = currentObject?.status === 'Draft';
      const isDeleteDisabled = !isPolicyDraft || hasReadOnlyAccess();
      
      commandBarItems.push(
        {
          key: "divider1",
          itemType: ContextualMenuItemType.Divider,
          onRender: ActionBarDivider,
        },
        {
          key: "delete",
          text: "Delete policy",
          iconProps: icons.delete,
          buttonStyles: buttonStyle,
          disabled: isDeleteDisabled,
          onClick: () => {
            if (onDeleteClick) {
              onDeleteClick();
            } else {
              openDeletionDialog({
                title: "Delete policy?",
                objectType: AgreementObjectType.policy,
                objectId: currentObject.id,
                objectEtag: currentObject.etag,
                policyStatus: currentObject.status,
                policyType: currentObject.type,
                cloud: environmentConfig.cloud,
                environment: environmentConfig.environment,
              });
            }
            trackEvent("Delete policy button clicked");
          },
        },
        {
          key: "divider2",
          itemType: ContextualMenuItemType.Divider,
          onRender: ActionBarDivider,
        }
      );

      // Show Withdraw button only for Published/Live policies (not 'Publish' status)
      if (isPolicyPublished && currentObject?.status !== 'Publish') {
        commandBarItems.push(
          {
            key: "withdraw",
            text: isWithdrawing ? "Withdrawing..." : "Withdraw",
            iconProps: icons.undo,
            buttonStyles: buttonStyle,
            disabled: isWithdrawDisabled || isWithdrawing,
            onClick: () => {
              setIsWithdrawing(true);
              withdrawPolicy({
                id: currentObject?.id,
                status: currentObject?.status,
                cloud: environmentConfig.cloud,
                environment: environmentConfig.environment,
                policyType: currentObject?.type,
              }).unwrap()
                .then(() => {
                  trackEvent("Policy withdrawn successfully - PR created");
                  // Navigate to Policies page after withdrawal
                  goToRoute(routeDefinitions[RouteComponent.Policies].getRouteInfo({}));
                })
                .catch(() => {
                  trackEvent("Policy withdraw failed");
                })
                .finally(() => {
                  setIsWithdrawing(false);
                });
            },
          },
          {
            key: "divider3",
            itemType: ContextualMenuItemType.Divider,
            onRender: ActionBarDivider,
          }
        );
      }

      // Publish button (only show for non-published policies)
      if (!isPolicyPublished) {
        // disablePublishOverride is calculated in PolicyEditHeader based on hasTemplatesWithCriteria from nodeTree
        // Additional checks for template and criteria on the policy object as fallback
        const hasTemplate = currentObject?.templateId || currentObject?.template;
        const hasCriteria = currentObject?.criteria && Array.isArray(currentObject.criteria) && currentObject.criteria.length > 0;
        // Disable if: disablePublishOverride is true OR no template OR no criteria
        const isPublishDisabled = disablePublishOverride || hasReadOnlyAccess() || !hasTemplate || !hasCriteria;
        commandBarItems.push({
          key: "publish",
          text: "Publish",
          iconProps: icons.upload,
          buttonStyles: buttonStyle,
          disabled: isPublishDisabled,
          onClick: () => {
            setIsPublishDialogOpen(true);
            trackEvent("Publish policy button clicked");
          },
        });
      }

      return commandBarItems;
    }

    // from the Info panel of Clause, Custom Clause, Template
    if (editState === undefined) {
      if (isDraftRevisionVariant(currentRevisionStatus)) {
        commandBarItems.push({
          key: "edit",
          text: "Edit",
          iconProps: icons.edit,
          buttonStyles: buttonStyle,
          onClick: () => {
            trackEvent(`${objectType} edit button clicked`);
            if (objectType === AgreementObjectType.template) {
              openEditPanel(currentObject?.id, currentRevision?.id);
            } else if (objectType === AgreementObjectType.customClause) {
              openEditPanel({
                templateId: currentObject?.templateId,
                clauseId: currentObject?.id,
                revisionId: currentRevision?.id,
              });
            } else {
              // Clause
              openEditPanel({
                clauseId: currentObject?.id,
                revisionId: currentRevision?.id,
              });
            }
          },
          disabled: hasReadOnlyAccess(),
        });
      }
      // template info panel has "Open" button when status is not Draft or Unpublished
      else if (objectType === AgreementObjectType.template) {
        commandBarItems.push(
          {
            key: "open",
            text: "Open",
            iconProps: icons.open,
            buttonStyles: buttonStyle,
            onClick: () => {
              openEditPanel(currentObject?.id, currentRevision?.id);
            },
          },
          {
            key: "divider",
            itemType: ContextualMenuItemType.Divider,
            // eslint-disable-next-line react/no-unstable-nested-components
            onRender: () => <VerticalDivider styles={dividerStyle} />,
          }
        );
      }
    }

    const isPublishedVariant = isPublishedRevisionVariant(
      currentRevisionStatus
    );
    const isEditNewRevision =
      editState === AgreementObjectEditState.NewRevision || hasReadOnlyAccess();

    const hasObjectContents =
      objectContentInfo &&
      !objectContentInfo.isLoading &&
      objectContentInfo.contents &&
      objectContentInfo.contents.length !== 0;

    const disablePublish =
      disablePublishOverride ||
      isEditNewRevision ||
      (objectContentInfo && !hasObjectContents) ||
      hasReadOnlyAccess();

    if (
      (objectType === AgreementObjectType.clause &&
        editState !== AgreementObjectEditState.NewClause) ||
      (objectType === AgreementObjectType.template &&
        editState !== AgreementObjectEditState.NewTemplate)
    ) {
      const isDeleteRevision =
        objectInfo.revisions && objectInfo.revisions?.length > 1;
      const deleteButtonText = isDeleteRevision
        ? "Delete draft"
        : `Delete ${objectType.toLowerCase()}`;

      if (
        !isPublishedVariant &&
        currentRevisionStatus !== RevisionStatus.Test
      ) {
        commandBarItems.push(
          ...[
            {
              key: "delete",
              text: deleteButtonText,
              iconProps: icons.delete,
              disabled: isEditNewRevision,
              buttonStyles: buttonStyle,
              onClick: () => {
                openDeletionDialog({
                  title: `${deleteButtonText}?`,
                  objectType,
                  objectId: currentObject.id,
                  objectEtag: currentObject.etag,
                  revisionId: isDeleteRevision
                    ? currentRevision?.id
                    : undefined,
                  revisionEtag: isDeleteRevision
                    ? currentRevision?.etag
                    : undefined,
                });
                trackEvent(
                  `Delete ${objectType.toLowerCase()} button clicked from ${routeInfo?.component
                  } page`
                );
              },
            },
            {
              key: "divider",
              itemType: ContextualMenuItemType.Divider,
              // eslint-disable-next-line react/no-unstable-nested-components
              onRender: () => <VerticalDivider styles={dividerStyle} />,
            },
          ]
        );

        if (
          currentRevisionStatus === RevisionStatus.Draft ||
          // unpublished templates have the "Test" button, unpublished clauses do not
          (currentRevisionStatus === RevisionStatus.Unpublished &&
            objectType === AgreementObjectType.template)
        ) {
          commandBarItems.push({
            key: "test",
            text: "Test",
            iconProps: icons.test,
            disabled: disablePublish,
            buttonStyles: buttonStyle,
            onClick: () => {
              openUpdateRevisionDialog({
                type: DialogContentType.Copy,
                ...updateRevisionDialogInfo,
              });
              trackEvent(
                `Test ${objectType.toLowerCase()} button clicked from ${routeInfo?.component
                } page`
              );
            },
          });
        }
      }

      if (
        currentRevisionStatus === RevisionStatus.Test ||
        currentRevisionStatus === RevisionStatus.Pending
      ) {
        commandBarItems.push({
          key: "withdraw",
          text: "Withdraw",
          iconProps: icons.undo,
          buttonStyles: buttonStyle,
          onClick: () => {
            openUpdateRevisionDialog({
              type: DialogContentType.Withdraw,
              ...updateRevisionDialogInfo,
            });
          },
          disabled: hasReadOnlyAccess(),
        });
      }

      if (!isPublishedVariant) {
        commandBarItems.push({
          key: "publish",
          text: "Publish",
          disabled: disablePublish,
          iconProps: icons.upload,
          buttonStyles: buttonStyle,
          onClick: () => {
            openUpdateRevisionDialog({
              type: DialogContentType.GoLive,
              ...updateRevisionDialogInfo,
            });
          },
        });
      }

      if (
        isPublishedVariant &&
        currentRevisionStatus !== RevisionStatus.Pending
      ) {
        commandBarItems.push({
          key: "createNewVersion",
          text: "Create New Version",
          iconProps: icons.add,
          buttonStyles: buttonStyle,
          onClick: () => {
            trackEvent(
              `${objectType} create new revision button clicked from ${routeInfo?.component} page`
            );
            if (objectType === AgreementObjectType.template) {
              openEditPanel(
                currentObject?.id,
                currentRevision?.id,
                AgreementObjectEditState.NewRevision
              );
            } else {
              openEditPanel(
                {
                  clauseId: currentObject?.id,
                  revisionId: currentRevision?.id,
                },
                AgreementObjectEditState.NewRevision
              );
            }
          },
          disabled: hasReadOnlyAccess(),
        });
      }
    }

    if (
      objectType === AgreementObjectType.customClause &&
      editState !== AgreementObjectEditState.NewClause &&
      !isPublishedVariant
    ) {
      commandBarItems.push({
        key: "publish",
        text: "Publish",
        disabled: disablePublish,
        iconProps: icons.upload,
        buttonStyles: buttonStyle,
        onClick: () => {
          openUpdateRevisionDialog({
            type: DialogContentType.customClauseGoLive,
            ...updateRevisionDialogInfo,
            templateId: currentObject.templateId,
            templateName: currentObject.templateName,
          });
        },
      });
    }

    return commandBarItems;
  };

  const handlePublish = async (publishData: IPolicyPublishData) => {
    // Format dates to UTC midnight ISO strings (e.g., "2026-02-28T00:00:00.000Z")
    const toUTCMidnightISO = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}T00:00:00.000Z`;
    };
    const contentRefreshDate = publishData.contentRefreshDate
      ? toUTCMidnightISO(new Date(publishData.contentRefreshDate))
      : toUTCMidnightISO(new Date());
    const agreementEffectiveDate = publishData.agreementEffectiveDate
      ? toUTCMidnightISO(new Date(publishData.agreementEffectiveDate))
      : toUTCMidnightISO(new Date());

    try {
      // Get template revisions to find the published revision
      const templateId = currentObject?.templateId;
      let templateRevisionId: string | undefined;
      let constraints: Array<{ propertyKey: string; propertySource: string; propertyValue: null }> = [];

      if (templateId) {
        const revisionsResult = await getTemplateRevisions(templateId).unwrap();
        // Find the first revision with status "Live" (published)
        const publishedRevision = revisionsResult?.find(
          (revision: ITemplateRevision) => revision.status === RevisionStatus.Live
        );
        templateRevisionId = publishedRevision?.id;

        // Fetch required constraints for the published revision
        if (templateRevisionId) {
          const constraintsResult = await getRequiredConstraints({
            templateId,
            id: templateRevisionId
          }).unwrap();

          // Transform constraints to the required format
          constraints = (constraintsResult || []).map((constraint: IRequiredConstraints) => ({
            propertyKey: constraint.constraintKeyName,
            propertySource: "Agreement",
            propertyValue: null
          }));
        }
      }

      // Call the publish policy mutation
      await publishPolicy({
        id: currentObject?.id,
        contentRefreshDate,
        agreementEffectiveDate,
        agreementSettings: publishData.agreementSettings,
        isInternalTestOnly: publishData.isInternalTestOnly,
        cloud: environmentConfig.cloud,
        environment: environmentConfig.environment,
        policyType: currentObject?.type,
        constraints,
      }).unwrap();

      setIsPublishDialogOpen(false);

      // Navigate to Policies page after publishing
      goToRoute(routeDefinitions[RouteComponent.Policies].getRouteInfo({}));
      trackEvent("Policy published successfully");
    } catch (error) {
      trackEvent("Policy publish failed");
      // Keep the dialog open so user can retry
    }
  };

  return (
    <>
      <ActionBar
        items={getCommandBarItems()}
        overflowItems={undefined}
        additionalStyles={additionalStyles?.actionBarStyles}
      />
      {componentInfo === "policyEdit" && (
        <PolicyPublishDialog
          isOpen={isPublishDialogOpen}
          onDismiss={() => setIsPublishDialogOpen(false)}
          onPublish={handlePublish}
          policyName={currentObject?.name || ""}
          templateName={policyDetails?.template || currentObject?.templateName || currentObject?.template || ""}
          initialContentRefreshDate={policyDetails?.contentRefreshDate}
          initialAgreementEffectiveDate={policyDetails?.agreementEffectiveDate}
          initialAgreementSettings={policyDetails?.agreementSettings}
        />
      )}
    </>
  );
};

export const SharedActionBarWithLoading = WithLoading(SharedActionBar);

export default SharedActionBar;
